'use client'

import { motion, AnimatePresence } from 'motion/react'
import { nodes } from '../../assets/routes'
import { useState } from 'react'
export default function Structure() {
    const [activeNode, setActiveNode] = useState<null | number>(null)

    const desktopIndent = 48 // px

    return (
        <section className='bg-[#0f0f0f] py-24 px-6 md:px-12 lg:px-24 overflow-hidden'>
            <div className='mx-auto max-w-4xl'>
                <div className='text-center md:text-left mb-16'>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className='mb-2 text-3xl text-center capitalize font-black tracking-tight text-white md:text-4xl'
                    >
                        Hierarchy
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className='text-[10px] text-center font-bold tracking-[0.2em] text-zinc-500'
                    >
                        A Top-Down Command Chain
                    </motion.p>
                </div>

                <div className='relative flex flex-col items-center md:items-start'>
                    {nodes.map((node, index) => {
                        const Icon = node.icon

                        return (
                            <div key={index} className='relative w-full flex flex-col items-center md:items-start'>
                                {/* Desktop L-Connector */}
                                {index > 0 && (
                                    <div
                                        className={`hidden md:block absolute top-[-48px] border-l-2 border-b-2 rounded-bl-2xl transition-colors duration-500 border-zinc-800`}
                                        style={{
                                            left: `${(index - 1) * desktopIndent + 24}px`,
                                            width: `${desktopIndent}px`,
                                            height: '72px'
                                        }}
                                    />
                                )}

                                <motion.div
                                    onClick={() => setActiveNode(activeNode === index ? null : index)}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    style={
                                        {
                                            '--ml': `${index * desktopIndent}px`,
                                            cursor: 'pointer'
                                        } as React.CSSProperties
                                    }
                                    className={`group relative flex w-full max-w-lg items-start gap-4 md:gap-6 rounded-xl border bg-[#141414] p-3 md:p-6 text-left transition-all duration-300 md:ml-[var(--ml)] ${
                                        activeNode === index
                                            ? 'border-[#b4ff00] shadow-[0_0_30px_rgba(180,255,0,0.05)]'
                                            : 'border-zinc-800 hover:border-zinc-700'
                                    }`}
                                >
                                    <div
                                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                            activeNode === index
                                                ? 'bg-[#b4ff00]/10'
                                                : 'bg-zinc-900 group-hover:bg-zinc-800'
                                        }`}
                                    >
                                        <Icon
                                            size={20}
                                            className={activeNode === index ? 'text-[#b4ff00]' : 'text-zinc-400'}
                                        />
                                    </div>
                                    <div className='flex-1'>
                                        <h3
                                            className={`mb-1 text-lg font-bold transition-colors ${
                                                activeNode === index ? 'text-[#b4ff00]' : 'text-white'
                                            }`}
                                        >
                                            {node.title}
                                        </h3>
                                        <p className='text-sm text-zinc-400 leading-relaxed'>{node.desc}</p>

                                        {/* Expanded Content */}
                                        <AnimatePresence initial={false}>
                                            {activeNode === index && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className='overflow-hidden'
                                                >
                                                    <div className='mt-4 pt-4 border-t border-zinc-800'>
                                                        <p className='text-sm text-zinc-300 mb-3'>{node.subText}</p>
                                                        <ul className='space-y-2'>
                                                            {node.subPoints?.map((point, i) => (
                                                                <li
                                                                    key={i}
                                                                    className='flex items-start text-sm text-zinc-400'
                                                                >
                                                                    <span className='mr-2 mt-1.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-[#b4ff00]' />
                                                                    {point}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    <div
                                        className={`mt-1 transition-transform ${
                                            activeNode === index
                                                ? 'text-[#b4ff00] rotate-90'
                                                : 'text-zinc-600 group-hover:translate-x-1'
                                        }`}
                                    >
                                        <svg
                                            width='20'
                                            height='20'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            stroke='currentColor'
                                            strokeWidth='2.5'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        >
                                            <path d='M9 18l6-6-6-6' />
                                        </svg>
                                    </div>
                                </motion.div>

                                {/* Mobile Connector Line */}
                                {index < nodes.length - 1 && (
                                    <motion.div
                                        initial={{ height: 0 }}
                                        whileInView={{ height: 32 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: index * 0.1 + 0.2 }}
                                        className={`md:hidden w-px my-2 ${index === 0 ? 'bg-[#b4ff00]' : 'bg-zinc-800'}`}
                                    />
                                )}

                                {/* Desktop Spacing */}
                                {index < nodes.length - 1 && <div className='hidden md:block h-12' />}
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
